<template>
  <ModalWindow v-model:isOpen="isModalOpen" blur :backgroundOpacity="0.8">
    <div class="code-window">
      <div class="code-window__title">{{ $t`Введите код из SMS` }}</div>
      <div class="code-window__info" v-html="smsTextInfo"></div>
      <div class="code-window__input">
        <input
          ref="smsInput"
          v-model="smsCode"
          @input="handleInput"
          type="tel"
        />
        <div class="mask-wrapper" ref="mask">
          <div class="mask-stick"></div>
          <div class="mask-stick"></div>
          <div class="mask-stick"></div>
          <div class="mask-stick"></div>
        </div>
      </div>
      <div v-if="isSmsResendable" class="code-window__resend--allowed">
        <button @click="resendSms">{{ $t`Отправить повторно` }}</button>
      </div>
      <div v-else class="code-window__resend--disabled">
        {{ remainingTimeText }}
      </div>
    </div>
  </ModalWindow>
</template>

<script>
import { sendSmsRequest, checkSmsRequest } from "@/api/sms";
import ModalWindow from "@/components/shared/modal/ModalWindow";

let timer = null;
const RESEND_TIME_SECONDS = 60;

export default {
  /**
   * @typedef {Object} SmsPayload
   * @property {string} otpCodeName !required!
   * other optional properties...
   */
  components: {
    ModalWindow,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    smsPayload: {
      /**
       * @type {import('vue').PropType<SmsPayload>}
       */
      type: Object,
      required: true,
    },
  },
  emits: ["valid-code", "update:isOpen"],
  data() {
    return {
      smsCode: "",
      remainingTime: RESEND_TIME_SECONDS,
      isModalOpen: false,
      isSmsResendable: false,
      phoneNumber: null,
    };
  },
  mounted() {
    if (this.isOpen && this.remainingTime === RESEND_TIME_SECONDS) {
      this.initSmsWindow();
    }
    window.mobapp_callbacks.onSmsCodeReceived = (code) => {
      if (typeof code !== "string") {
        code = code.toString();
      }

      if (code.length !== 4) {
        return;
      }

      this.stopReadingSms();

      this.smsCode = code;
      this.fillAllMasks();
      this.checkSms();
    };
  },
  beforeUnmount() {
    this.stopReadingSms();
    this.resetAllMasks();
    if (timer) {
      clearInterval(timer);
    }
  },
  computed: {
    remainingTimeText() {
      return this.$l10nformat(this.$t`Отправить повторно через #SECONDS# сек`, {
        SECONDS: this.remainingTime,
      });
    },
    smsTextInfo() {
      return this.$l10nformat(
        this.$t`На #NUMBER# отправлен SMS с кодом подтверждения.`,
        {
          NUMBER: this.formattedPhoneNumber,
        }
      );
    },
    formattedPhoneNumber() {
      return this.phoneNumber?.replace(
        /(\d{3})(\d{3})(\d{2})(\d{2})/,
        "+7 ($1) $2-$3-$4"
      );
    },
  },
  methods: {
    async initSmsWindow() {
      if (this.remainingTime === RESEND_TIME_SECONDS) {
        const { phone_number } = await sendSmsRequest(this.smsPayload);
        this.phoneNumber = phone_number;
        this.startReadingSms();
        this.startTimer();
      }
      this.isModalOpen = true;
      this.$nextTick(() => {
        this.$refs.smsInput.focus();
      });
    },
    async resendSms() {
      await sendSmsRequest(this.smsPayload);
      this.startReadingSms();
      this.remainingTime = RESEND_TIME_SECONDS;

      this.resetAllMasks();
      this.smsCode = "";

      this.startTimer();
      this.isSmsResendable = false;

      this.$nextTick(() => {
        this.$refs.smsInput.focus();
      });
    },
    startTimer() {
      timer = setInterval(() => {
        if (this.remainingTime <= 1) {
          this.isSmsResendable = true;
          clearInterval(timer);
        }
        this.remainingTime -= 1;
      }, 1000);
    },
    fillAllMasks() {
      this.$refs.mask.childNodes.forEach((node) => {
        node.classList.add("filled");
      });
    },
    resetAllMasks() {
      this.$refs.smsInput.value = "";
      this.smsCode = "";
      this.$refs.mask.childNodes.forEach((node) => {
        node.classList.remove("filled");
      });
    },
    async checkSms() {
      document.activeElement.blur();
      try {
        const response = await checkSmsRequest(this.smsCode);
        this.$emit("update:isOpen", false);
        this.$emit("valid-code", response.otp_hash);
      } catch {
        this.resetAllMasks();
      }
    },
    handleInput(e) {
      e.target.value = this.smsCode.replace(/(\d{4})(.+)/, "$1");
      this.smsCode = e.target.value;

      if (this.smsCode.length === 4) {
        this.stopReadingSms();
        this.fillAllMasks();
        this.checkSms();
        return;
      }

      let nodeIndex = this.smsCode.length - 1;
      if (e.inputType === "deleteContentBackward") {
        nodeIndex = this.smsCode.length;
      }

      const currentMaskStick = this.$refs.mask.childNodes[nodeIndex];
      if (currentMaskStick) {
        currentMaskStick.classList.toggle("filled");
      }
    },
    startReadingSms() {
      if (window.mobapp && window.mobapp.startReadingSms) {
        window.mobapp.startReadingSms(
          this.remainingTime,
          "window.mobapp_callbacks.onSmsCodeReceived"
        );
      }
    },
    stopReadingSms() {
      if (window.mobapp?.stopReadingSms) {
        window.mobapp.stopReadingSms();
      }
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.initSmsWindow();
      } else {
        this.isModalOpen = false;
      }
    },
    isModalOpen(val) {
      this.$emit("update:isOpen", val);
    },
  },
};
</script>

<style lang="scss" scoped>
.code-window {
  font-family: Roboto-Regular;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 30px 15px;

  &__title {
    color: white;
    font-size: 17px;
    font-family: Roboto-Bold;
    margin-bottom: 30px;
  }

  &__info {
    color: #9b9b9b;
    font-size: 14px;
    margin-bottom: 20px;
    padding: 0 55px;
    word-break: normal;
  }

  &__input {
    height: 80px;
    width: 190px;
    margin-bottom: 30px;

    input {
      margin: 0;
      min-width: 220px;
      overflow: hidden;
      padding-left: 8px;
      height: inherit;
      width: inherit;
      color: white;
      background-color: transparent;
      border: none;
      outline: none;
      font-size: 50px;
      line-height: 58.6px;
      letter-spacing: 20px;
    }

    .mask-wrapper {
      display: flex;
      justify-content: center;

      .mask-stick {
        width: 40px;
        height: 2px;
        background-color: red;
        margin: 0 5px;

        &.filled {
          background-color: #4a4a4a;
        }
      }
    }
  }

  &__resend--allowed {
    button {
      width: 100%;
      padding: 5px 14px;
      border: 1px solid white;
      border-radius: 2px;
      font-size: 14px;
      outline: none;
      background-color: transparent;
      color: white;
    }
  }

  &__resend--disabled {
    font-size: 14px;
    color: white;
  }
}
</style>
